
import { useState, useEffect } from "react"

import { useDispatch } from "react-redux"

import { Grid, List, ListItem, ListItemText, Typography, Box, Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions, Divider, Select, MenuItem, FormControl, InputLabel, IconButton, TextField, CircularProgress } from "@mui/material"

import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';

import moment from "moment"

import { formatCurrency } from "../helpers/FormatCurrency";

import { InfoField } from "./InfoField";

import { DialogDocumento } from "../dialogs/DialogDocumento";
import { DialogPartesExpediente } from "../dialogs/DialogPartesExpediente";
import { DialogPrestacionesExpediente } from "../dialogs/DialogPrestacionesExpediente";

import { DialogContentStyle, DialogActionsStyle } from "../styles/DialogStyle";

import { openSnackbar } from "../store/slices/SnackbarSlice"

import { ObtenerTipoJuicio } from "../connections/catalogos/TipoJuicioConnection";
import { EditarExpediente } from "../connections/recepcion/ExpedienteConnection";

import { ExpedienteProps } from "../interfaces/recepcion/ExpedienteInterface"
import { DemandaInicialProps } from "../interfaces/recepcion/DemandaInicialInterface";
import { TipoJuicioProps } from "../interfaces/catalogos/TipoJuicioInterface";
import { AcuerdoProps } from "../interfaces/tribunal/AcuerdoInterface";

interface Props {
    expediente?:        ExpedienteProps;
    demandaInicial?:    DemandaInicialProps;
    setAcuerdo?:        React.Dispatch<React.SetStateAction<AcuerdoProps>>
    edit?:              boolean;
    SolicitarFecha?:    boolean;
}

export const BoxInformacionExpediente = ( { expediente, demandaInicial, setAcuerdo, SolicitarFecha = false, edit = false }: Props ) => {

    const dispatch = useDispatch();

    const [openAnexos, setOpenAnexos] = useState( false );
    const [selectedAnexoUrl, setSelectedAnexoUrl] = useState('');
    const [openDocumento, setOpenDocumento] = useState( false );

    const [openDialogPartes, setOpenDialogPartes] = useState( false );
    const [openDialogPrestaciones, setOpenDialogPrestaciones] = useState( false );

    const [tipoJuicio, setTipoJuicio] = useState( 0 );
    const [tipoJuicioArray, setTipoJuicioArray] = useState<TipoJuicioProps[]>( [] );

    const [cuantia, setCuantia] = useState( expediente?.cuantia ?? '' );

    const [actores, setActores] = useState<string[]>( expediente?.actores ?? [] );
    const [demandados, setDemandados] = useState<string[]>( expediente?.demandados ?? [] );
    const [prestaciones, setPrestaciones] = useState<string[]>( demandaInicial?.prestaciones ?? [] );

    const [loadingTipoJuicio, setLoadingTipoJuicio] = useState( false );
    const [loadingCuantia, setLoadingCuantia] = useState( false );

    const handleGuardarExpediente = async ( campo: 'tipoJuicio' | 'cuantia' ) => {

        const setLoading = campo === 'tipoJuicio' ? setLoadingTipoJuicio : setLoadingCuantia;

        setLoading( true );

        await EditarExpediente({ idExpediente: expediente?.idExpediente ?? 0, idTipoJuicio: tipoJuicio, cuantia, }).then( resp => {

            setTimeout(() => {

                const { success, message } = resp;

                if( success ){

                    const tipoJuicioDesc = tipoJuicioArray.find( (t) => t.idTipoJuicio === tipoJuicio )?.descripcion ?? '';

                    dispatch( openSnackbar({ message, variant: 'success' }) );

                    setAcuerdo?.( prev => ({
                        ...prev,
                        expediente: {
                            ...prev.expediente,
                            idTipoJuicio: tipoJuicio,
                            tipoJuicio:   tipoJuicioDesc,
                            cuantia,
                        }
                    }));

                } else {

                    dispatch( openSnackbar({ message, variant: 'error' }) );

                }

                setLoading( false );

            }, 700);

        });

    }

    const handleCloseAnexos = () => {

        setOpenAnexos( false );
        setSelectedAnexoUrl( '' );

    }

    useEffect(() => {

        async function Obtener() {

            await ObtenerTipoJuicio().then( resp => { 
                setTipoJuicioArray( resp ) 
            });

        }

        if( edit ){
            Obtener();
        }

    }, [ edit ]);

    useEffect(() => {

        if( tipoJuicioArray.length !== 0 ){
            setTipoJuicio( expediente?.idTipoJuicio ?? 0 );
        }

    }, [ expediente?.idTipoJuicio, tipoJuicioArray.length ]);

    useEffect(() => {

        setCuantia( expediente?.cuantia ?? '' );

    }, [ expediente?.cuantia ]);

    useEffect(() => {

        setActores( expediente?.actores ?? [] );
        setDemandados( expediente?.demandados ?? [] );

    }, [ expediente?.actores, expediente?.demandados ]);

    useEffect(() => {

        setPrestaciones( demandaInicial?.prestaciones ?? [] );

    }, [ demandaInicial?.prestaciones ]);

    return (

        <Box p={4} component={ Paper }>

            <Grid container spacing={3}>

                {
                    demandaInicial?.folioBuzonElectronico
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            <InfoField
                                label="Folio buzón electrónico"
                                value={ demandaInicial?.folioBuzonElectronico }
                                fontSize={ 16 }
                            />

                        </Grid>
                }

                {
                    ( demandaInicial && demandaInicial?.anexosBuzonElectronico.length !== 0 )
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            <List disablePadding>

                                <ListItem sx={{ py: 0, px: 0 }}>

                                    <ListItemText
                                        sx={{ mr: 2 }}
                                        primary={ <Typography variant='subtitle2' sx={{ color: 'text.secondary' }}> Anexo(s) buzón electrónico: </Typography> }
                                        secondary={
                                            <Button
                                                fullWidth
                                                variant='contained'
                                                sx={{ mt: 1 }}
                                                onClick={ () => setOpenAnexos(true) }
                                            >
                                                Anexo(s)
                                            </Button>
                                        }
                                    />

                                </ListItem>

                            </List>

                        </Grid>
                }

                {
                    expediente?.numeroFolio
                    ?
                        <Grid size={{ md: 6, xs: 12 }}>

                            <InfoField
                                label="Número de expediente"
                                value={
                                    <>
                                        { expediente?.numeroExpediente }
                                        { expediente?.numeroFolio && <small style={{ fontSize: 14 }}> (F. { expediente?.numeroFolio })</small> }
                                    </>
                                }
                                fontSize={ 16 }
                            />

                        </Grid>
                    :
                        <Grid size={{ md: 6, xs: 12 }}>

                            <InfoField
                                label="Número de expediente"
                                value={ expediente?.numeroExpediente }
                                fontSize={ 16 }
                            />

                        </Grid>
                }

                {
                    expediente?.fecha
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            <InfoField
                                label="Fecha"
                                value={ moment( new Date( expediente?.fecha ) ).format( 'DD-MM-YYYY' ) }
                                fontSize={ 16 }
                            />

                        </Grid>
                }

                {
                    ( expediente?.tipoJuicio || edit )
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            {
                                ( edit && !SolicitarFecha )
                                ?
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

                                        <FormControl fullWidth>

                                            <InputLabel shrink>Tipo de juicio</InputLabel>

                                            <Select
                                                notched
                                                label="Tipo de juicio"
                                                value={ tipoJuicio }
                                                onChange={ (e) => setTipoJuicio( e.target.value as number ) }
                                            >
                                                <MenuItem value={0}>Selecciona una opción</MenuItem>

                                                {
                                                    tipoJuicioArray.map( ({ idTipoJuicio, descripcion }) => (
                                                        <MenuItem key={ idTipoJuicio } value={ idTipoJuicio }>{ descripcion }</MenuItem>
                                                    ))
                                                }

                                            </Select>

                                        </FormControl>
                                       
                                        <IconButton
                                            color='primary'
                                            onClick={ () => handleGuardarExpediente( 'tipoJuicio' ) }
                                            disabled={ loadingTipoJuicio || tipoJuicio === ( expediente?.idTipoJuicio ?? 0 ) }
                                        >
                                            { loadingTipoJuicio ? <CircularProgress size={ 20 } /> : <SaveIcon /> }
                                        </IconButton>                                      

                                    </Box>
                                :
                                    <InfoField
                                        label="Tipo de juicio"
                                        value={ expediente?.tipoJuicio }
                                        fontSize={ 16 }
                                    />
                            }

                        </Grid>
                }

                {
                    ( expediente?.cuantia || edit )
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            {
                                edit
                                ?
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

                                        <TextField
                                            fullWidth
                                            label="Cuantía"
                                            value={ cuantia }
                                            onChange={ (e) => setCuantia( e.target.value ) }
                                            slotProps={{ inputLabel: { shrink: true } }}
                                        />

                                        <IconButton
                                            color='primary'
                                            onClick={ () => handleGuardarExpediente( 'cuantia' ) }
                                            disabled={ loadingCuantia || cuantia === ( expediente?.cuantia ?? '' ) }
                                        >
                                            { loadingCuantia ? <CircularProgress size={ 20 } /> : <SaveIcon /> }
                                        </IconButton>

                                    </Box>
                                :
                                    expediente?.cuantia
                                    &&
                                        <InfoField
                                            label="Cuantía"
                                            value={ formatCurrency( expediente?.cuantia ) }
                                            fontSize={ 16 }
                                        />
                            }

                        </Grid>
                }

                {
                    ( expediente?.etapa && expediente?.subEtapa )
                    ?
                        <Grid size={{ md: 6, xs: 12 }}>

                            <InfoField
                                label="Etapa"
                                value={ `${ expediente?.etapa } (${ expediente?.subEtapa })` }
                                fontSize={ 16 }
                            />

                        </Grid>
                    :
                        expediente?.etapa
                        &&
                            <Grid size={{ md: 6, xs: 12 }}>

                                <InfoField
                                    label="Etapa"
                                    value={ expediente?.etapa }
                                    fontSize={ 16 }
                                />

                            </Grid>
                }

                {
                    expediente?.juez
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            <InfoField
                                label="Juez"
                                value={ expediente?.juez }
                                fontSize={ 16 }
                            />

                        </Grid>
                }

                {
                    expediente?.secretario
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            <InfoField
                                label="Secretario(a) admisibilidad"
                                value={ expediente?.secretario }
                                fontSize={ 16 }
                            />

                        </Grid>
                }

                {
                    expediente?.secretarioTramite
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            <InfoField
                                label="Secretario(a) trámite (Mayor cuantía)"
                                value={ expediente?.secretarioTramite }
                                fontSize={ 16 }
                            />

                        </Grid>
                }

                {
                    expediente?.secretarioTramiteMenor
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            <InfoField
                                label="Secretario(a) trámite (Menor cuantía)"
                                value={ expediente?.secretarioTramiteMenor }
                                fontSize={ 16 }
                            />

                        </Grid>
                }

                {
                    expediente?.secretarioEjecucion
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            <InfoField
                                label="Secretario(a) ejecución"
                                value={ expediente?.secretarioEjecucion }
                                fontSize={ 16 }
                            />

                        </Grid>
                }

                {
                    expediente?.juzgadoOrigen
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            <InfoField
                                label="Juzgado de origen"
                                value={ expediente?.juzgadoOrigen }
                                fontSize={ 16 }
                            />

                        </Grid>
                }

                {
                    ( actores.length !== 0 || edit )
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            <List disablePadding>

                                <ListItem sx={{ py: 0, px: 0 }}>

                                    <ListItemText
                                        sx={{ mr: 2 }}
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        color: "text.secondary",
                                                        letterSpacing: 0.5,
                                                        fontWeight: 700,
                                                    }}>
                                                        Actor(es):
                                                </Typography>

                                                {
                                                    edit
                                                    &&
                                                        <IconButton
                                                            size='small'
                                                            color='primary'
                                                            onClick={ () => setOpenDialogPartes( true ) }
                                                        >
                                                            <EditIcon fontSize='small' />
                                                        </IconButton>
                                                }

                                            </Box>
                                        }
                                        secondary={
                                            <List disablePadding>

                                                {
                                                    actores.map( (actor, index) => (

                                                        <ListItem key={ index } sx={{ py: 0, px: 0 }}>
                                                            <Typography sx={{ color: 'black' }} variant='subtitle2' fontWeight='bold' fontSize={ 16 }>{ actor }</Typography>
                                                        </ListItem>

                                                    ))
                                                }

                                            </List>
                                        }
                                        slotProps={{
                                            primary: { component: 'div' },
                                            secondary: { component: 'div' }
                                        }}
                                    />

                                </ListItem>

                            </List>

                        </Grid>
                }

                {
                    ( demandados.length !== 0 || edit )
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            <List disablePadding>

                                <ListItem sx={{ py: 0, px: 0 }}>

                                    <ListItemText
                                        sx={{ mr: 2 }}
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        color: "text.secondary",
                                                        letterSpacing: 0.5,
                                                        fontWeight: 700,
                                                    }}>
                                                        Demandado(s):
                                                </Typography>

                                                {
                                                    edit
                                                    &&
                                                        <IconButton
                                                            size='small'
                                                            color='primary'
                                                            onClick={ () => setOpenDialogPartes( true ) }
                                                        >
                                                            <EditIcon fontSize='small' />
                                                        </IconButton>
                                                }

                                            </Box>
                                        }
                                        secondary={
                                            <List disablePadding>

                                                {
                                                    demandados.map( (demandado, index) => (

                                                        <ListItem key={ index } sx={{ py: 0, px: 0 }}>
                                                            <Typography sx={{ color: 'black' }} variant='subtitle2' fontWeight='bold' fontSize={ 16 }>{ demandado }</Typography>
                                                        </ListItem>

                                                    ))
                                                }

                                            </List>
                                        }
                                        slotProps={{
                                            primary: { component: 'div' },
                                            secondary: { component: 'div' }
                                        }}
                                    />

                                </ListItem>

                            </List>

                        </Grid>
                }

                {
                    ( prestaciones.length !== 0 || edit )
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            <List disablePadding>

                                <ListItem sx={{ py: 0, px: 0 }}>

                                    <ListItemText
                                        sx={{ mr: 2 }}
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        color: "text.secondary",
                                                        letterSpacing: 0.5,
                                                        fontWeight: 700,
                                                    }}>
                                                        Prestación(es) o Pretención(es):
                                                </Typography>

                                                {
                                                    ( edit && !SolicitarFecha )
                                                    &&
                                                        <IconButton
                                                            size='small'
                                                            color='primary'
                                                            onClick={ () => setOpenDialogPrestaciones( true ) }
                                                        >
                                                            <EditIcon fontSize='small' />
                                                        </IconButton>
                                                }

                                            </Box>
                                        }
                                        secondary={
                                            <List disablePadding>

                                                {
                                                    prestaciones.map( (prestacion, index) => (

                                                        <ListItem key={ index } sx={{ py: 0, px: 0 }}>
                                                            <Typography sx={{ color: 'black' }} variant='subtitle2' fontWeight='bold' fontSize={ 16 }>{ prestacion }</Typography>
                                                        </ListItem>

                                                    ))
                                                }

                                            </List>
                                        }
                                        slotProps={{
                                            primary: { component: 'div' },
                                            secondary: { component: 'div' }
                                        }}
                                    />

                                </ListItem>

                            </List>

                        </Grid>
                }

                {
                    demandaInicial?.digitalizacion
                    &&
                        <Grid size={{ md: 12, xs: 12 }}>

                            <List disablePadding>

                                <ListItem sx={{ py: 0, px: 0 }}>

                                    <ListItemText
                                        sx={{ mr: 2 }}
                                        primary={
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    color: "text.secondary",
                                                    letterSpacing: 0.5,
                                                    fontWeight: 700,
                                                }}>
                                                    Digitalización:
                                            </Typography>
                                        }
                                    />

                                </ListItem>

                            </List>

                            <iframe
                                title='iframe-documento'
                                style={{ width: '100%', height: 800, borderRadius: 8 }}
                                src={ demandaInicial?.digitalizacion ?? '' }
                            />

                        </Grid>
                }

                {
                    demandaInicial?.acuseBuzonElectronico
                    &&
                        <Grid size={{ md: 12, xs: 12 }}>

                            <List disablePadding>

                                <ListItem sx={{ py: 0, px: 0 }}>

                                    <ListItemText
                                        sx={{ mr: 2 }}
                                        primary={
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    color: "text.secondary",
                                                    letterSpacing: 0.5,
                                                    fontWeight: 700,
                                                }}>
                                                    Acuse de envió buzón electronico:
                                            </Typography>
                                        }
                                    />

                                </ListItem>

                            </List>

                            <iframe
                                title='iframe-documento'
                                style={{ width: '100%', height: 800, borderRadius: 8 }}
                                src={ demandaInicial?.acuseBuzonElectronico ?? '' }
                            />

                        </Grid>
                }

            </Grid>

            <Dialog
                fullWidth
                maxWidth="md"
                open={ openAnexos }
                onClose={ handleCloseAnexos }
            >

                <DialogTitle>
                    Anexo(s) buzón electrónico
                </DialogTitle>

                <DialogContent sx={ DialogContentStyle }>

                    <List disablePadding>

                        {
                            demandaInicial?.anexosBuzonElectronico.map( (anexo, index) => (

                                <Box key={ index }>

                                    <ListItem sx={{ py: 2, px: 0 }}>

                                        <ListItemText
                                            primary={ <Typography variant='subtitle2' fontWeight='bold'>{ anexo.archivo }</Typography> }
                                            slotProps={{ primary: { component: 'div' }, secondary: { component: 'div' } }}
                                        />

                                        <Button
                                            variant='contained'
                                            size='small'
                                            onClick={ () => { setSelectedAnexoUrl( anexo.url ); setOpenDocumento( true ); } }
                                        >
                                            Ver
                                        </Button>

                                    </ListItem>

                                    {
                                        index < ( demandaInicial?.anexosBuzonElectronico.length ?? 0 ) - 1
                                        &&
                                            <Divider />
                                    }

                                </Box>

                            ))
                        }

                    </List>

                </DialogContent>

                <DialogActions sx={ DialogActionsStyle }>

                    <Button color='secondary' onClick={ handleCloseAnexos }>
                        Cerrar
                    </Button>

                </DialogActions>

            </Dialog>

            <DialogDocumento
                documento={ selectedAnexoUrl }
                openDialog={ openDocumento }
                handleCloseDialog={ () => setOpenDocumento( false ) }
            />

            <DialogPartesExpediente
                idExpediente={ expediente?.idExpediente ?? 0 }
                openDialog={ openDialogPartes }
                handleCloseDialog={ () => setOpenDialogPartes( false ) }
                onGuardar={ ( a, d ) => {
                    setActores( a );
                    setDemandados( d );
                    setAcuerdo?.( prev => ({
                        ...prev,
                        expediente: {
                            ...prev.expediente,
                            actores: a,
                            demandados: d,
                        }
                    }));
                }}
            />

            <DialogPrestacionesExpediente
                idExpediente={ expediente?.idExpediente ?? 0 }
                idTipoJuicio={ expediente?.idTipoJuicio ?? 0 }
                openDialog={ openDialogPrestaciones }
                handleCloseDialog={ () => setOpenDialogPrestaciones( false ) }
                onGuardar={ ( p, medioPreparatorio ) => {
                    setPrestaciones( p );
                    setAcuerdo?.( prev => ({
                        ...prev,
                        demandaInicial: {
                            ...prev.demandaInicial,
                            prestaciones: p,
                        },
                        expediente: {
                            ...prev.expediente,
                            medioPreparatorio,
                        }
                    }));
                }}
            />

        </Box>
    )
}
